import { ProductDTO } from "@agtech/data/operations/OperationData";
import { SaleLotCategory } from "../lots/categories/LotCategoryData";
import { SaleDiscount } from "./discounts/SaleDiscountData";
import { AgTechQuery } from "@agtech/core/data/queries/AgTechDataQueries";
import { SaleFeatures } from "./SaleData";
import { AgTechHttpResponses } from "@agtech/core/data/actions/AgTechHttpRequests";

/// DTOs

export type SaleSelectionDTO = {
    saleId: number;
    saleName: string;
    saleStartDate: Date,
    saleEndDate: Date,
}

type SaleInformationDTO = {
    saleId: number,
    saleName: string,
    saleStartDate: Date,
    saleEndDate: Date,
}

type SaleBidderDTO = {
    bidderId: number,
    bidderNumber: string,
    contactId: number,
    date: Date,
    saleId: number
}

type SaleLotDTO = {
    lotId: number,
    lotNumber: string,
    saleOrder: number,
    lotDescription: string,
    bidderId: number,
    bidderNumber: string,
    purchasePrice: number,
    timestamp: string,
    invoiceState: number,
    isSplit: boolean
}

type SaleDetailDTO = {
    sale: SaleInformationDTO,
    sales: SaleSelectionDTO[],
    bidders: SaleBidderDTO[],
    lots: SaleLotDTO[],
    lotCategories: SaleLotCategory[],
    discounts: SaleDiscount[]
}

/// Queries

export const SaleDataGetSaleDataQuery: AgTechQuery<{ saleId: number }, SaleDetailDTO> = {
    queryKey: () => ['SaleData'],
    query: (props) => {
        return props.http.get({
            request: {
                path: 'Sales/Data/' + props.inputData.saleId
            }
        });
    },
    content: {
        loadingText: 'Loading sale data...',
        failureText: 'Failed to load sale data.'
    }
}

export const SaleDataGetSalesQuery: AgTechQuery<any, SaleSelectionDTO[]> = {
    queryKey: () => ['SaleSelections'],
    query: (props) => {
        return props.http.get({
            request: 'Sales/SalesDropdown'
        });
    },
    content: {
        loadingText: 'Loading sale data...',
        failureText: 'Failed to load sale data.'
    }
}

export type SalePurchaseDataDTO = {
    paymentSecret: string,
    product: ProductDTO
}

export const SaleDataGetSalePaymentSecretQuery: AgTechQuery<{ saleId: number, productId: number }, SalePurchaseDataDTO> = {
    queryKey: () => ['SalePaymentSecret'],
    query: (props) => {
        return props.http.getEntity<SalePurchaseDataDTO>({
            request: `Sales/${props.inputData.saleId}/${props.inputData.productId}/PaymentSecret`
        });
    },
    content: {
        loadingText: 'Loading sale checkout...',
        failureText: 'Failed to load sale checkout.'
    },
    settings: () => ({
        staleTime: 0
    })
}

// QUERY: Sale License Status 

export const SaleDataIsSaleLicensedQuery: AgTechQuery<{ saleId: number }, boolean> = {
    queryKey: inputData => ['SaleLicense', inputData.saleId],
    query: async (props) => {
        const isSaleLicensingEnabled = props.features.isFeatureEnabled(SaleFeatures.EnforceLicensing);

        return isSaleLicensingEnabled ? await props.http.getEntity<{ status: number }, boolean>({
            request: `Sales/${props.inputData.saleId}/LicenseStatus`,
            convert: data => {
                return data.status === 3;
            }
        }) : AgTechHttpResponses.Success(true);
    },
    content: {
        loadingText: 'Checking sale license...',
        failureText: 'Failed to check sale license.'
    },
    settings: () => ({
        staleTime: 0
    })
}